import {
    Component,
    Input,
    Output,
    EventEmitter,
    OnChanges,
    SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

/**
 * Componente genérico de filtro para listas de elementos
 *
 * Este componente proporciona una funcionalidad de búsqueda y filtrado en tiempo real
 * sobre arrays de objetos. Emite eventos cuando los elementos filtrados cambian.
 *
 * @example
 * ```html
 * <app-generic-filter
 *   [items]="users"
 *   placeholder="Buscar usuarios..."
 *   (filtered)="onFilteredUsers($event)">
 * </app-generic-filter>
 * ```
 *
 * @since 1.0.0
 * @version 1.0.0
 */
@Component({
    selector: 'app-generic-filter',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './generic-filter.component.html',
    styleUrls: ['./generic-filter.component.css'],
})
export class GenericFilterComponent implements OnChanges {
    /**
     * Array de elementos a filtrar
     *
     * El componente realizará la búsqueda sobre todos los valores de las propiedades
     * de cada objeto en este array.
     *
     * @default []
     * @required
     */
    @Input() items: any[] = [];

    /**
     * Texto placeholder para el input de búsqueda
     *
     * @default 'Buscar...'
     */
    @Input() placeholder = 'Buscar...';

    /**
     * Evento emitido cuando los elementos han sido filtrados
     *
     * Emite un array con los elementos que coinciden con el criterio de búsqueda.
     * Si no hay texto de búsqueda, emite todos los elementos originales.
     *
     * @emits any[] - Array de elementos filtrados
     */
    @Output() filtered = new EventEmitter<any[]>();

    /**
     * Texto actual de búsqueda
     *
     * @internal
     */
    text = '';

    /**
     * Método del ciclo de vida que se ejecuta cuando cambian las propiedades de entrada
     *
     * @param changes - Objeto que contiene los cambios en las propiedades @Input
     * @inheritdoc
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (changes['items']) {
            this.filtrar();
        }
    }

    /**
     * Maneja el evento de cambio en el input de búsqueda
     *
     * Se ejecuta cada vez que el usuario escribe en el campo de búsqueda
     * y dispara el proceso de filtrado.
     *
     * @public
     */
    onInputChange() {
        this.filtrar();
    }

    /**
     * Limpia el texto de búsqueda y restaura la lista original
     *
     * Este método establece el texto de búsqueda a vacío y emite
     * todos los elementos sin filtrar.
     *
     * @public
     */
    clear() {
        this.text = '';
        this.filtrar();
    }

    /**
     * Convierte cualquier valor a string para la búsqueda
     *
     * Maneja diferentes tipos de datos incluyendo objetos, null y undefined.
     * Para objetos, intenta convertirlos a JSON string.
     *
     * @param value - Valor a convertir a string
     * @returns Representación en string del valor
     * @private
     */
    private valueToString(value: any): string {
        if (value === null || value === undefined) return '';
        if (typeof value === 'object') {
            try {
                return JSON.stringify(value);
            } catch {
                return String(value);
            }
        }
        return String(value);
    }

    /**
     * Realiza el filtrado de los elementos basado en el texto de búsqueda
     *
     * Compara el texto de búsqueda (en minúsculas) con todos los valores
     * de las propiedades de cada objeto. Si no hay texto de búsqueda,
     * emite todos los elementos originales.
     *
     * @private
     */
    private filtrar() {
        if (!this.text.trim()) {
            this.filtered.emit(this.items);
            return;
        }

        const search = this.text.toLowerCase();
        const filtrados = this.items.filter((item) =>
            Object.values(item).some((value) =>
                this.valueToString(value).toLowerCase().includes(search)
            )
        );
        this.filtered.emit(filtrados);
    }
}
